/**
 * PromisedPaymentsPanel — A reusable inline panel that lets the user attach a
 * Payment Plan to a client. Two modes:
 *   • Custom dates (up to 6)
 *   • Weekly recurring (pick a weekday)
 *
 * Used in:
 *   - Quick Reminders client row    (source="quick_reminders", source_id=<reminderId>)
 *   - FTC Builder balance step      (source="ftc_report",      source_id=<reportId>)
 *
 * Renders the list of existing plans for the client + an "Add plan" button
 * that opens an inline form. Saves to /api/promised-payments.
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { CalendarDays, Plus, X, Trash2, Pause, Play, Send, AlertTriangle, Calendar } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  return v === Math.round(v) ? `$${v.toLocaleString()}` : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export default function PromisedPaymentsPanel({
  clientName,
  clientEmail,
  defaultAmount = "",
  source = "quick_reminders",
  sourceId = null,
  compact = false,
}) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    amount: defaultAmount ? String(defaultAmount).replace(/[^0-9.]/g, "") : "",
    mode: "custom",
    custom_dates: ["", "", "", "", "", ""],
    weekly_day: 4, // Friday default
    notes: "",
  });

  const loadPlans = async () => {
    if (!clientName) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/promised-payments`, { params: { client_name: clientName } });
      setPlans(r.data || []);
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  };

  useEffect(() => { loadPlans(); }, [clientName]); // eslint-disable-line

  const reset = () => {
    setForm({
      amount: defaultAmount ? String(defaultAmount).replace(/[^0-9.]/g, "") : "",
      mode: "custom",
      custom_dates: ["", "", "", "", "", ""],
      weekly_day: 4,
      notes: "",
    });
    setShowForm(false);
  };

  const filledDates = useMemo(() => form.custom_dates.filter((d) => !!d), [form.custom_dates]);

  const save = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter a payment amount"); return; }
    if (form.mode === "custom" && filledDates.length === 0) { toast.error("Pick at least one date"); return; }
    if (form.mode === "weekly" && (form.weekly_day === null || form.weekly_day === "")) { toast.error("Pick a weekday"); return; }
    try {
      const payload = {
        client_name: clientName,
        client_email: clientEmail || null,
        amount: amt,
        mode: form.mode,
        custom_dates: form.mode === "custom" ? filledDates : [],
        weekly_day: form.mode === "weekly" ? Number(form.weekly_day) : null,
        source,
        source_id: sourceId || null,
        notes: form.notes || null,
      };
      await axios.post(`${API}/api/promised-payments`, payload);
      toast.success(`Payment plan saved for ${clientName}`);
      reset();
      await loadPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save plan");
    }
  };

  const togglePlan = async (id) => {
    try {
      await axios.post(`${API}/api/promised-payments/${id}/toggle`);
      await loadPlans();
    } catch (e) { toast.error("Failed to toggle plan"); }
  };

  const deletePlan = async (id, name) => {
    if (!window.confirm(`Remove this payment plan for ${name}?`)) return;
    try {
      await axios.delete(`${API}/api/promised-payments/${id}`);
      toast.success("Plan removed");
      await loadPlans();
    } catch (e) { toast.error("Failed to delete plan"); }
  };

  const sendTestNow = async (id) => {
    try {
      const r = await axios.post(`${API}/api/promised-payments/${id}/send-now`);
      toast.success(`Test reminders sent to ${(r.data.sent_to || []).filter(Boolean).join(", ")}`);
    } catch (e) { toast.error("Failed to send test reminder"); }
  };

  if (!clientName) {
    return (
      <div className="rounded-lg border border-amber-700/40 bg-amber-900/15 p-3 text-amber-200 text-xs">
        Enter the client&apos;s name first to set up a payment plan.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-zinc-700 bg-zinc-900/60 p-4 space-y-3" data-testid="promised-payments-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-emerald-400" />
          <h4 className="text-sm font-bold text-white">Payment Plan</h4>
          {plans.length > 0 && (
            <span className="text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-500/15 border border-emerald-500/40 rounded-full px-2 py-0.5">
              {plans.filter((p) => p.active).length} active
            </span>
          )}
        </div>
        {!showForm && (
          <Button
            size="sm"
            onClick={() => setShowForm(true)}
            className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
            data-testid="add-promised-plan-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add plan
          </Button>
        )}
      </div>

      {/* Existing plans */}
      {!loading && plans.length === 0 && !showForm && (
        <p className="text-xs text-zinc-500">
          No payment plan yet. Add up to 6 specific dates, or set a weekly recurring day.
          Reminders fire at 9 AM Pacific to both you and {clientEmail || "the client"}.
        </p>
      )}

      {plans.map((p) => (
        <div
          key={p.id}
          className={`rounded-lg border ${p.active ? "border-emerald-500/40 bg-emerald-500/5" : "border-zinc-700 bg-zinc-900/40 opacity-70"} p-3`}
          data-testid={`plan-${p.id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-white">{fmtMoney(p.amount)}</span>
                {p.mode === "weekly" ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/40">
                    Weekly · every {WEEKDAYS[p.weekly_day]}
                  </span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40">
                    Custom · {p.custom_dates.length} date{p.custom_dates.length === 1 ? "" : "s"}
                  </span>
                )}
                {!p.active && (
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 bg-zinc-800 border border-zinc-700 rounded-full px-2 py-0.5">
                    Paused
                  </span>
                )}
              </div>
              {p.mode === "custom" && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {p.custom_dates.map((d) => (
                    <span key={d} className="text-[11px] bg-zinc-800 border border-zinc-700 text-zinc-200 px-2 py-0.5 rounded">
                      {fmtDate(d)}
                    </span>
                  ))}
                </div>
              )}
              {p.notes && <p className="text-[11px] text-zinc-400 mt-2 italic">&ldquo;{p.notes}&rdquo;</p>}
              {p.last_sent_at && (
                <p className="text-[10px] text-zinc-500 mt-1">
                  Last sent: {new Date(p.last_sent_at).toLocaleString()}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <button
                onClick={() => sendTestNow(p.id)}
                title="Send a test reminder right now"
                className="text-[10px] text-emerald-300 hover:text-emerald-200 flex items-center gap-1"
                data-testid={`plan-test-${p.id}`}
              >
                <Send className="w-3 h-3" /> Test
              </button>
              <button
                onClick={() => togglePlan(p.id)}
                title={p.active ? "Pause reminders" : "Resume reminders"}
                className="text-[10px] text-zinc-300 hover:text-white flex items-center gap-1"
                data-testid={`plan-toggle-${p.id}`}
              >
                {p.active ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                {p.active ? "Pause" : "Resume"}
              </button>
              <button
                onClick={() => deletePlan(p.id, p.client_name)}
                title="Delete plan"
                className="text-[10px] text-rose-300 hover:text-rose-200 flex items-center gap-1"
                data-testid={`plan-delete-${p.id}`}
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Create form */}
      {showForm && (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-emerald-300 uppercase tracking-wider">
              New plan for {clientName}
            </p>
            <button onClick={reset} className="text-zinc-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Mode toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setForm({ ...form, mode: "custom" })}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border ${form.mode === "custom" ? "bg-purple-500/20 border-purple-500/60 text-purple-200" : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-white"}`}
              data-testid="mode-custom-btn"
            >
              <Calendar className="w-3.5 h-3.5 inline mr-1" /> Custom dates (up to 6)
            </button>
            <button
              onClick={() => setForm({ ...form, mode: "weekly" })}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border ${form.mode === "weekly" ? "bg-blue-500/20 border-blue-500/60 text-blue-200" : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-white"}`}
              data-testid="mode-weekly-btn"
            >
              <CalendarDays className="w-3.5 h-3.5 inline mr-1" /> Weekly recurring
            </button>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Payment amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
              <input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="150"
                className="w-full pl-7 pr-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="plan-amount-input"
              />
            </div>
          </div>

          {/* Mode-specific fields */}
          {form.mode === "custom" && (
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">
                Promised dates (fill in 1 to 6 — leave the rest blank)
              </label>
              <div className={compact ? "grid grid-cols-2 gap-2" : "grid grid-cols-3 gap-2"}>
                {form.custom_dates.map((d, idx) => (
                  <input
                    key={idx}
                    type="date"
                    value={d}
                    onChange={(e) => {
                      const nd = [...form.custom_dates];
                      nd[idx] = e.target.value;
                      setForm({ ...form, custom_dates: nd });
                    }}
                    className="px-2 py-1.5 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    data-testid={`plan-date-${idx}`}
                  />
                ))}
              </div>
              <p className="text-[10px] text-zinc-500 mt-1">
                Reminders fire at 9 AM Pacific on each date.
              </p>
            </div>
          )}

          {form.mode === "weekly" && (
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Day of week</label>
              <div className="grid grid-cols-7 gap-1">
                {WEEKDAYS.map((d, idx) => (
                  <button
                    key={d}
                    onClick={() => setForm({ ...form, weekly_day: idx })}
                    className={`px-1 py-2 rounded-lg text-[10px] font-semibold border ${form.weekly_day === idx ? "bg-blue-500/20 border-blue-500/60 text-blue-200" : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-white"}`}
                    data-testid={`weekday-${idx}`}
                  >
                    {d.slice(0, 3)}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-zinc-500 mt-1">
                Fires every {WEEKDAYS[form.weekly_day]} at 9 AM Pacific until you pause or delete it.
              </p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. partial payment for credit-repair retainer"
              className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
              data-testid="plan-notes-input"
            />
          </div>

          {!clientEmail && (
            <div className="rounded-lg border border-amber-700/40 bg-amber-900/15 p-2 text-amber-200 text-[11px] flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>No client email on file — reminders will go to clearallcreditrepair@gmail.com only.</span>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              onClick={save}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
              data-testid="save-plan-btn"
            >
              Save plan
            </Button>
            <Button variant="outline" onClick={reset} className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 text-zinc-200">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
